/**
 * masterGanttProjectRows.ts
 * Project-grouped layout for the Master Gantt (the default "Group by project").
 *
 * Split out of masterGanttRowBuilder.ts (600-line cap) when BUG-2026-0194 added
 * the per-machine / per-test-type and per-person drill-down levels the lab
 * asked for. The bar builders (buildBookingPoint / buildAssignmentPoint) and the
 * date helpers still live in masterGanttRowBuilder.ts and are reused here, so
 * every leaf bar keeps its id / custom keys byte-for-byte (conflict tagging,
 * deep-links and the drawer are untouched).
 *
 * Row hierarchy (Highcharts treegrid via `id` / `parent`):
 *
 *   Project                 id = "proj-{projectKey}"
 *   └─ Work Order           id = "wo-{woKey}"
 *      ├─ Machines          id = "machines-{woKey}"                        (group band)
 *      │  └─ Machine        id = "machsum-{woKey}-{machineKey}"            (collapsed; "RK-SAW — Saw · 4 tasks · 6 days")
 *      │     ├─ Test type   id = "machtt-{woKey}-{machineKey}-{slug}"      (collapsed; only when >1 booking of that type)
 *      │     │  └─ Booking  id = "booking-{bookingKey}"
 *      │     └─ Booking     id = "booking-{bookingKey}"                    (a type with a single booking is the leaf itself)
 *      └─ Staff             id = "staff-{woKey}"                           (group band)
 *         ├─ Person         id = "person-{woKey}-{userKey}"                (collapsed; "S. Hegger · 6 tasks · 10%")
 *         │  └─ Task bar    id = "assign-{firstWeeklyKey}"                 (weekly rows merged — masterGanttAssignmentMerge)
 *         └─ Task bar       id = "assign-{firstWeeklyKey}"                 (a person with a single task is the leaf itself)
 *
 * Design notes (BUG-2026-0194):
 *   - Summary rows start COLLAPSED so a work order opens as "one row per machine,
 *     one row per person" — the total work is the envelope bar; drilling in
 *     shows the split. Highcharts applies `collapsed` only on first render, so
 *     the 60 s auto-refresh never undoes what the user expanded.
 *   - A level with exactly one child is flattened (the child becomes the row)
 *     so no click ever reveals a single duplicate bar.
 *   - Ids are derived from stable keys/slugs (never array indexes) so a data
 *     refresh keeps the treegrid structure signature — and the user's
 *     expand/collapse state — intact.
 */

import type {
  MasterGanttMachineBooking,
  MasterGanttWorkOrder,
  MasterGanttProject,
} from '../../types/schedule.js';

import {
  resolvePointFill,
  type GanttFill,
  type GanttTaskType,
} from './ganttFillResolver.js';

import {
  buildBookingPoint,
  resolveOpacity,
  toMs,
  toEndMs,
  minDate,
  maxDate,
  type GanttPoint,
  type GanttColorTokens,
} from './masterGanttRowBuilder.js';

import {
  assignmentSpan,
  groupAssignmentsByPerson,
  buildMergedAssignmentPoint,
  personRowLabel,
  machineRowLabel,
  testTypeRowLabel,
  bookingTestType,
  slugify,
} from './masterGanttAssignmentMerge.js';

import { buildPrepPointsForBucket } from './masterGanttPrepMerge.js';

// ---------------------------------------------------------------------------
// Row-id scheme
// ---------------------------------------------------------------------------

export function machinesGroupRowId(woKey: string): string { return `machines-${woKey}`; }
export function staffGroupRowId(woKey: string): string { return `staff-${woKey}`; }
export function machineSummaryRowId(woKey: string, machineKey: string): string {
  return `machsum-${woKey}-${machineKey}`;
}
export function testTypeRowId(woKey: string, machineKey: string, testType: string): string {
  return `machtt-${woKey}-${machineKey}-${slugify(testType)}`;
}
export function personRowId(woKey: string, userKey: string): string {
  return `person-${woKey}-${userKey}`;
}

// ---------------------------------------------------------------------------
// Grouping helpers (pure)
// ---------------------------------------------------------------------------

export interface MachineBookingBucket {
  machineKey: string;
  machineCode: string;
  machineName: string;
  /** Ordered by start date. */
  bookings: MasterGanttMachineBooking[];
}

/** One bucket per machine (ordered by machine code), bookings by start date. */
export function groupBookingsByMachine(bookings: MasterGanttMachineBooking[]): MachineBookingBucket[] {
  const map = new Map<string, MachineBookingBucket>();
  for (const bk of bookings) {
    let bucket = map.get(bk.machineKey);
    if (!bucket) {
      bucket = { machineKey: bk.machineKey, machineCode: bk.machineCode, machineName: bk.machineName, bookings: [] };
      map.set(bk.machineKey, bucket);
    }
    bucket.bookings.push(bk);
  }
  const buckets = Array.from(map.values()).sort((a, b) => a.machineCode.localeCompare(b.machineCode));
  for (const b of buckets) {
    b.bookings.sort((x, y) => (x.startDate < y.startDate ? -1 : x.startDate > y.startDate ? 1 : x.bookingKey.localeCompare(y.bookingKey)));
  }
  return buckets;
}

export interface TestTypeBucket {
  testType: string;
  bookings: MasterGanttMachineBooking[];
}

/** One bucket per test type (task name), in order of first appearance. */
export function groupBookingsByTestType(bookings: MasterGanttMachineBooking[]): TestTypeBucket[] {
  const map = new Map<string, TestTypeBucket>();
  for (const bk of bookings) {
    const testType = bookingTestType(bk);
    let bucket = map.get(testType);
    if (!bucket) {
      bucket = { testType, bookings: [] };
      map.set(testType, bucket);
    }
    bucket.bookings.push(bk);
  }
  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// Envelope row factory
// ---------------------------------------------------------------------------

function envelopeRow(
  id: string,
  parent: string | undefined,
  name: string,
  taskType: GanttTaskType,
  starts: (string | null | undefined)[],
  ends: (string | null | undefined)[],
  colors: GanttColorTokens,
  custom: Record<string, unknown> | undefined,
  collapsed: boolean,
): GanttPoint {
  const fill: GanttFill = resolvePointFill(taskType, null, null, colors);
  return {
    id,
    parent,
    name,
    start: toMs(minDate(...starts) ?? undefined),
    // BUG-2026-0095: inclusive end → exclusive end-of-day so the envelope
    // spans the full inclusive range of its children.
    end: toEndMs(maxDate(...ends) ?? undefined),
    color: fill,
    opacity: resolveOpacity(taskType),
    collapsed,
    taskType,
    status: null,
    eventType: null,
    custom,
  };
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** Machines band → per-machine summary → per-test-type → bookings. */
function buildMachinesSection(wo: MasterGanttWorkOrder, woId: string, colors: GanttColorTokens): GanttPoint[] {
  if (wo.machineBookings.length === 0) return [];
  const points: GanttPoint[] = [];
  const groupId = machinesGroupRowId(wo.workOrderKey);

  points.push(envelopeRow(
    groupId, woId, 'Machines', 'machinesGroup',
    wo.machineBookings.map((b) => b.startDate),
    wo.machineBookings.map((b) => b.endDate),
    colors, undefined, false,
  ));

  // BUG-2026-0193: the work order's rock prep (Cutting / Grinding / Conformity,
  // one booking per test line across the saw / grinder / gauge) folds into ONE
  // "Preparation" bar directly under the Machines band — "one total bar shown
  // as one task". Only the non-prep bookings go through the per-machine tree.
  const { prepPoints, rest } = buildPrepPointsForBucket(wo.machineBookings, groupId, colors, false);
  points.push(...prepPoints);

  for (const machine of groupBookingsByMachine(rest)) {
    const machineId = machineSummaryRowId(wo.workOrderKey, machine.machineKey);
    points.push(envelopeRow(
      machineId, groupId,
      machineRowLabel(machine.machineCode, machine.machineName, machine.bookings),
      'machineSummary',
      machine.bookings.map((b) => b.startDate),
      machine.bookings.map((b) => b.endDate),
      colors,
      { machineKey: machine.machineKey, workOrderKey: wo.workOrderKey },
      true,
    ));

    for (const tt of groupBookingsByTestType(machine.bookings)) {
      if (tt.bookings.length === 1) {
        // Single booking of this type → it IS the row (no duplicate level).
        points.push(buildBookingPoint(tt.bookings[0], machineId, colors));
        continue;
      }
      const ttId = testTypeRowId(wo.workOrderKey, machine.machineKey, tt.testType);
      points.push(envelopeRow(
        ttId, machineId,
        testTypeRowLabel(tt.testType, tt.bookings),
        'machineSummary',
        tt.bookings.map((b) => b.startDate),
        tt.bookings.map((b) => b.endDate),
        colors,
        { machineKey: machine.machineKey, workOrderKey: wo.workOrderKey, testType: tt.testType },
        true,
      ));
      for (const bk of tt.bookings) {
        points.push(buildBookingPoint(bk, ttId, colors));
      }
    }
  }
  return points;
}

/** Staff band → per-person summary → merged (person, task) bars. */
function buildStaffSection(wo: MasterGanttWorkOrder, woId: string, colors: GanttColorTokens): GanttPoint[] {
  if (wo.employeeAssignments.length === 0) return [];
  const points: GanttPoint[] = [];
  const groupId = staffGroupRowId(wo.workOrderKey);
  const spans = wo.employeeAssignments.map(assignmentSpan);

  points.push(envelopeRow(
    groupId, woId, 'Staff', 'staffGroup',
    spans.map((s) => s.startDate),
    spans.map((s) => s.endDate),
    colors, undefined, false,
  ));

  for (const person of groupAssignmentsByPerson(wo.employeeAssignments)) {
    if (person.tasks.length === 1) {
      // One task → the merged task bar is the person's row.
      points.push(buildMergedAssignmentPoint(person.tasks[0], groupId, colors));
      continue;
    }
    const personId = personRowId(wo.workOrderKey, person.userKey);
    points.push(envelopeRow(
      personId, groupId,
      personRowLabel(person),
      'personSummary',
      [person.startDate],
      [person.endDate],
      colors,
      { userKey: person.userKey, workOrderKey: wo.workOrderKey },
      true,
    ));
    for (const task of person.tasks) {
      points.push(buildMergedAssignmentPoint(task, personId, colors));
    }
  }
  return points;
}

// ---------------------------------------------------------------------------
// Work order / project
// ---------------------------------------------------------------------------

export function buildWorkOrderPoints(
  wo: MasterGanttWorkOrder,
  projectId: string,
  colors: GanttColorTokens,
): GanttPoint[] {
  const woId = `wo-${wo.workOrderKey}`;
  const woRow = envelopeRow(
    woId, projectId,
    wo.workOrderName ? `${wo.workOrderNumber}: ${wo.workOrderName}` : wo.workOrderNumber,
    'workOrder',
    [wo.startDate ?? undefined],
    [wo.endDate ?? undefined],
    colors,
    { workOrderKey: wo.workOrderKey },
    false,
  );
  return [
    woRow,
    ...buildMachinesSection(wo, woId, colors),
    ...buildStaffSection(wo, woId, colors),
  ];
}

export function buildProjectPoints(
  proj: MasterGanttProject,
  colors: GanttColorTokens,
): GanttPoint[] {
  const projId = `proj-${proj.projectKey}`;
  const projRow = envelopeRow(
    projId, undefined,
    `${proj.projectId}: ${proj.projectName}`,
    'project',
    proj.workOrders.map((wo) => wo.startDate),
    proj.workOrders.map((wo) => wo.endDate),
    colors,
    { projectKey: proj.projectKey },
    false,
  );
  const points: GanttPoint[] = [projRow];
  for (const wo of proj.workOrders) {
    points.push(...buildWorkOrderPoints(wo, projId, colors));
  }
  return points;
}
